import { defineStore } from 'pinia'
import i18next from 'i18next'
import {
  differenceInYears,
  parse,
  format,
} from 'date-fns'

import { getDateFnsLocaleByIso } from 'www/utils'
import {
  getPatient,
  getPatientList,
} from 'www/vue_components/common/AppointmentPage/interfaces'
import {
  AGE_OF_MAJORITY,
  DEFAULT_PATIENTS_DATA,
  REQUIRED_FIO_DESCRIPTION,
  APPOINTMENT_ERRORS,
} from 'www/vue_components/common/AppointmentPage/constants'
import {
  getFioValidationRules,
  getPartialPatientData,
  getBirthDayValidationRulesAllSpecialties,
} from 'www/vue_components/common/AppointmentPage/functions'
import useLpuDataStore from 'www/vue_components/common/AppointmentPage/stores/useLpuDataStore'
import useGlobalDataStore from 'www/vue_components/common/AppointmentPage/stores/useGlobalDataStore'
// eslint-disable-next-line import/no-cycle
import useDoctorDataStore from 'www/vue_components/common/AppointmentPage/stores/useDoctorDataStore'
// eslint-disable-next-line import/no-cycle
import useAppointmentDataStore from 'www/vue_components/common/AppointmentPage/stores/useAppointmentDataStore'
import apiFamilyProfiles from 'www/vue_components/common/FamilyProfiles/api/apiFamilyProfiles'

const useFamilyProfilesStore = defineStore('familyProfilesStore', {
  state: () => ({
    selectedPatient: getPatient(),
    patientList: getPatientList(),
    isFamilyRequestDone: false,
    isSelectedPatient: false,
    patientCardEl: null,
    patientFormRef: null,
    patientFormFieldRefs: [],
    modalContentEl: null,
    isErrorState: false,
    isSavingProfile: false,
  }),
  getters: {
    fioValidationRules() {
      const getFioRules = requiredDescription => {
        const {
          onlyRu,
          onlyLetters,
          maxLength,
          required,
        } = getFioValidationRules(requiredDescription)

        const fioRules = window.IS_YDOC ? onlyLetters : onlyRu

        // Последовательность имеет значение
        return [required, maxLength, fioRules]
      }

      return {
        name: getFioRules(REQUIRED_FIO_DESCRIPTION.name),
        surname: getFioRules(REQUIRED_FIO_DESCRIPTION.surname),
        patronymic: getFioRules(REQUIRED_FIO_DESCRIPTION.patronymic),
      }
    },
    birthDayValidationRules() {
      const doctorDataStore = useDoctorDataStore()
      const { appointmentObjectTypes } = useAppointmentDataStore()
      const { required, ageLimit } = getBirthDayValidationRulesAllSpecialties(
        doctorDataStore.rangeAgeLimitAllSpecialities,
        { isServices: appointmentObjectTypes.isServices },
      )

      // Последовательность имеет значение
      return [required, ageLimit]
    },
    genderValidationRules() {
      return [val => val === 'F' || val === 'M']
    },
    isValidFio() {
      const isValidPatronymic = this.selectedPatient.hasPatronymic
        ? this.fioValidationRules.patronymic.every(rule => rule(this.selectedPatient.patronymic) === true)
        : true

      return this.fioValidationRules.name.every(rule => rule(this.selectedPatient.name) === true)
        && this.fioValidationRules.surname.every(rule => rule(this.selectedPatient.surname) === true)
        && isValidPatronymic
    },
    isValidBirthDay() {
      return this.birthDayValidationRules.every(rule => rule(this.selectedPatient.birthDay) === true)
    },
    invalidBirthDayText() {
      return this.isValidBirthDay
        ? null
        : this.birthDayValidationRules
          .map(rule => (typeof rule(this.selectedPatient.birthDay) === 'string' ? rule(this.selectedPatient.birthDay) : null))
          .find(i => typeof i === 'string')
    },
    isValidPatient() {
      return this.isValidFio && this.isValidBirthDay && this.isSelectedPatient
    },
    patientAge() {
      if (!this.isValidBirthDay) {
        return 0
      }

      const now = new Date()
      const parsedBirthDay = parse(
        this.selectedPatient.birthDay,
        'dd.MM.yyyy',
        now,
        { locale: getDateFnsLocaleByIso(i18next.language) },
      )

      return differenceInYears(now, parsedBirthDay)
    },
    isUnderagePatient() {
      if (!this.isValidBirthDay) {
        return false
      }

      return this.patientAge < AGE_OF_MAJORITY
    },
  },
  actions: {
    updateSelectedPatient(payload) {
      this.selectedPatient = {
        ...this.selectedPatient,
        ...payload,
      }
    },
    updatePatientList(payload) {
      this.patientList = payload
    },
    updateIsFamilyRequestDone(payload) {
      this.isFamilyRequestDone = !!payload
    },
    updateIsSelectedPatient(payload) {
      this.isSelectedPatient = !!payload
    },
    updatePatientCardEl(payload) {
      this.patientCardEl = payload
    },
    updatePatientFormRef(payload) {
      this.patientFormRef = payload
    },
    updatePatientFormFieldRefs(payload) {
      this.patientFormFieldRefs = payload
    },
    updateModalContentEl(payload) {
      this.modalContentEl = payload
    },
    updateIsErrorState(payload) {
      this.isErrorState = payload
    },

    validatePatientCard() {
      this.updateIsErrorState(!this.isValidPatient)

      if (!this.isValidPatient) {
        return this.patientCardEl
      }
    },
    async saveFamilyProfile(profile) {
      this.isSavingProfile = true

      try {
        const patientData = {
          first_name: profile.name,
          second_name: profile.patronymic,
          last_name: profile.surname,
          gender: profile.gender,
          birthday: format(
            parse(
              profile.birthDay,
              'dd.MM.yyyy',
              new Date(),
              { locale: getDateFnsLocaleByIso(i18next.language) },
            ),
            'yyyy-MM-dd',
          ),
          who_visited: '',
        }

        if (profile.uuid) {
          await apiFamilyProfiles.patch({
            uuid: profile.uuid,
            ...patientData,
          })
        } else {
          const { uuid } = await apiFamilyProfiles.post(patientData)

          // eslint-disable-next-line require-atomic-updates
          profile.uuid = uuid || null
        }

        return true
      } catch (error) {
        const { status } = error || {}
        const globalDataStore = useGlobalDataStore()

        if (status === 403) {
          globalDataStore.updateErrorData({
            isEnabled: true,
            isDialogRender: true,
            isPersistent: true,
            ...APPOINTMENT_ERRORS.notAuthorization,
          })
        }

        return false
      } finally {
        this.isSavingProfile = false
      }
    },
    async requestFamilyProfiles() {
      const lpuDataStore = useLpuDataStore()
      const appointmentDataStore = useAppointmentDataStore()

      const getDefaultMainPatient = () => ({
        ...getPatient(),
        ...DEFAULT_PATIENTS_DATA.main,
        hasPatronymic: true,
      })

      const setDefaultPatientList = () => {
        const mainPatient = getDefaultMainPatient()

        this.updatePatientList([
          { ...mainPatient },
          {
            ...getPatient(),
            ...DEFAULT_PATIENTS_DATA.other,
          },
        ])
      }

      try {
        const familyProfiles = await apiFamilyProfiles.get({
          lpuId: lpuDataStore.lpuData.id,
          appointmentTypeName: appointmentDataStore.appointmentTypeName,
        })

        const now = new Date()

        const patientList = familyProfiles.map(({
          uuid = '',
          gender = '',
          last_name: surname = '',
          first_name: name = '',
          second_name: patronymic = '',
          birthday: birthDay,
          is_main: isMain = false,
        }, index) => ({
          id: index,
          uuid,
          name,
          surname,
          gender,
          patronymic,
          hasPatronymic: !!patronymic || (!name && !surname && !patronymic),
          isMain,
          isOther: false,
          isEmptyMain: false,
          ...getPartialPatientData({
            name,
            surname,
            patronymic,
            birthDay: birthDay
              ? parse(birthDay, 'yyyy-MM-dd', now, { locale: getDateFnsLocaleByIso(i18next.language) })
              : null,
          }),
        }))

        // Сортируем, чтобы главный профиль стал первым
        patientList.sort(a => (a.isMain ? -1 : 1))

        if (patientList.length) {
          const mainPatient = patientList[0].isMain
            ? patientList[0]
            : getDefaultMainPatient()

          this.updatePatientList([
            { ...mainPatient },
            ...patientList.slice(1),
            {
              ...getPatient(),
              ...DEFAULT_PATIENTS_DATA.other,
            },
          ])
        } else {
          setDefaultPatientList()
        }
      } catch (error) {
        if (error?.response?.status === 423) {
          const globalDataStore = useGlobalDataStore()

          globalDataStore.updateErrorData({
            isEnabled: true,
            title: 'Клиника ограничила для вас онлайн-запись',
            titleMaxWidth: '350px',
            buttonState: {
              get: appointmentDataStore.appointmentObjectTypes.isDoctor ? 'toListDoctor' : 'toListLpu',
            },
          })

          return
        }

        setDefaultPatientList()
      }

      this.updateIsFamilyRequestDone(true)
    },
  },
})

export default useFamilyProfilesStore
